import axios from 'axios';
import { message } from 'antd';
import { clearAuthStorage, getLoginPath, getStoredToken, getStoredUser } from '../utils/auth';
import { getStoredZoneId } from '../utils/zone';

const API_BASE_URL = '/api';

export function compactParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== '')
  );
}

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json'
  }
});

let isRedirectingToLogin = false;

const getRequestRole = (config = {}) => {
  const requestUrl = config.url || '';
  // 代理工作台接口必须使用代理身份令牌
  if (requestUrl.startsWith('/auth/agent')
    || requestUrl.startsWith('/agents/sub-agents')
    || requestUrl.startsWith('/agents/account-search')
    || requestUrl.startsWith('/agents/recharge-logs')
    || requestUrl.startsWith('/agents/order-lookup')
    || requestUrl.startsWith('/manual-recharge/agent')
    || requestUrl.startsWith('/cs-desk')) {
    return 'agent';
  }
  return 'admin';
};

api.interceptors.request.use(
  (config) => {
    const requestRole = getRequestRole(config);
    const token = getStoredToken(requestRole);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const zoneId = getStoredZoneId();
    if (zoneId) {
      config.headers['X-Zone-Id'] = zoneId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const errorMessage = error.response?.data?.message || '';
    const requestUrl = error.config?.url || '';
    const isLoginRequest = [
      '/auth/login',
      '/auth/unified-login',
      '/auth/agent-login',
      '/promotions/public/login',
      '/login/account-login',
      '/login/client-login'
    ].some((url) => requestUrl.includes(url));
    const isPublicUserRequest = [
      '/mall/public',
      '/platform-currency/public',
      '/user-center/public',
      '/manual-recharge/public',
      '/login/web-session'
    ].some((url) => requestUrl.includes(url));
    // 会话已死(必须重新登录)的 403: 过期/无效 JWT, 或代理账号被禁用/删除。
    // 仅匹配这些后端明确消息 —— 从而: (1) CDN 边缘 403 是 HTML 错误页(data 为字符串, 无 .message),
    //   errorMessage='' 不命中 -> 不误判为登录失效(满足"别把 CDN 偶发错当登录失效");
    // (2) 管理端权限不足 403(Admin access required / Insufficient permissions / 只读越权)
    //   会话仍有效, 不在此集合 -> 维持原 message.error, 不会把在线管理员踢下线。
    const isSessionDead403 =
      status === 403 &&
      /invalid or expired token|agent account disabled or not found/i.test(errorMessage);
    const isTokenExpiredError =
      !isLoginRequest &&
      !isPublicUserRequest &&
      (status === 401 || isSessionDead403);

    if (isTokenExpiredError) {
      const requestRole = getRequestRole(error.config);
      const role = getStoredUser(requestRole)?.role || requestRole;
      const loginPath = getLoginPath(role);

      clearAuthStorage(role);

      if (!isRedirectingToLogin && window.location.pathname !== loginPath) {
        isRedirectingToLogin = true;
        message.warning('登录已过期，请重新登录');
        window.location.replace(loginPath);
      }
    } else if (error.response?.data?.message) {
      message.error(error.response.data.message);
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  unifiedLogin: (credentials) => api.post('/auth/unified-login', credentials),
  agentLogin: (credentials) => api.post('/auth/agent-login', credentials),
  getMe: () => api.get('/auth/me'),
  getZones: () => api.get('/auth/zones'),
  getAgentMe: () => api.get('/auth/agent/me'),
  changePassword: (data) => api.post('/auth/change-password', data),
  getUsers: () => api.get('/auth/users'),
  createUser: (data) => api.post('/auth/users', data)
};

export const agentAPI = {
  getList: (params) => api.get('/agents', { params }),
  getDetail: (id, params) => api.get(`/agents/${id}`, { params: compactParams(params) }),
  getTree: (params) => api.get('/agents/tree', { params: compactParams(params) }),
  create: (data) => api.post('/agents', data),
  update: (id, data) => api.put(`/agents/${id}`, data),
  ban: (id) => api.post(`/agents/${id}/ban`),
  unban: (id) => api.post(`/agents/${id}/unban`),
  delete: (id) => api.delete(`/agents/${id}`),
  generateCode: (prefix) => api.post('/agents/generate-code', { prefix }),
  getStats: () => api.get('/agents/stats/overview'),
  getSubAgents: (params) => api.get('/agents/sub-agents', { params: compactParams(params) }),
  getSubAgentDetail: (id, params) => api.get(`/agents/sub-agents/${id}`, { params: compactParams(params) }),
  createSubAgent: (data) => api.post('/agents/sub-agents', data),
  updateSubAgent: (id, data) => api.put(`/agents/sub-agents/${id}`, data),
  getSubAgentStats: () => api.get('/agents/sub-agents/stats'),
  searchAccounts: (params) => api.get('/agents/account-search', { params: compactParams(params) }),
  getRechargeLogs: (params) => api.get('/agents/recharge-logs', { params: compactParams(params) }),
  lookupOrders: (params) => api.get('/agents/order-lookup', { params: compactParams(params) })
};

export const promotionAPI = {
  login: (credentials) => api.post('/promotions/public/login', credentials),
  getCenter: (promotionToken) => api.get('/promotions/public/center', { params: { promotionToken } }),
  getWalletLogs: (promotionToken, params) => api.get('/promotions/public/wallet-logs', { params: compactParams({ ...params, promotionToken }) }),
  getOverview: () => api.get('/promotions/admin/overview'),
  getPromoters: (params) => api.get('/promotions/admin/promoters', { params: compactParams(params) }),
  getRebates: (params) => api.get('/promotions/admin/rebates', { params: compactParams(params) }),
  getConfig: () => api.get('/promotions/admin/config'),
  updateConfig: (data) => api.put('/promotions/admin/config', data)
};

export const serverAPI = {
  getStatus: (params) => api.get('/servers/status', { params }),
  getStatusStreamUrl: (token, zoneId) => {
    const searchParams = new URLSearchParams({
      token: token || ''
    });
    if (zoneId) {
      searchParams.set('zoneId', zoneId);
    }
    return `${API_BASE_URL}/servers/status/stream?${searchParams.toString()}`;
  },
  getLogStreamUrl: (serverId, params = {}, token) => {
    const searchParams = new URLSearchParams({
      token: token || '',
      type: params.type || 'startup',
      lines: String(params.lines || 100)
    });
    if (params.zoneId) {
      searchParams.set('zoneId', params.zoneId);
    }
    return `${API_BASE_URL}/servers/${serverId}/logs/stream?${searchParams.toString()}`;
  },
  start: (serverId) => api.post(`/servers/start/${serverId}`),
  stop: (serverId) => api.post(`/servers/stop/${serverId}`),
  restart: (serverId) => api.post(`/servers/restart/${serverId}`),
  getLogs: (serverId, params) => api.get(`/servers/${serverId}/logs`, { params }),
  startAll: () => api.post('/servers/start-all'),
  stopAll: () => api.post('/servers/stop-all')
};

// 用户管理"所有区": 逐玩家操作按该玩家所属区路由 —— 显式 target_zone(该行 server_id)随请求下发,
// 后端据此 runWithServerId 到对应区库/游戏服。未传 target_zone 时后端回退当前请求区(向后兼容)。
export const playerAPI = {
  getList: (params) => api.get('/players', { params }),
  getStats: () => api.get('/players/stats'),
  getBilling: (playerId, params, targetZone) => api.get(`/players/${playerId}/billing`, { params: compactParams({ ...params, target_zone: targetZone }) }),
  getDetail: (playerId, targetZone) => api.get(`/players/${playerId}`, { params: compactParams({ target_zone: targetZone }) }),
  getSafeCode: (playerId, targetZone) => api.get(`/players/${playerId}/safe-code`, { params: compactParams({ target_zone: targetZone }) }),
  getIntegralConfig: () => api.get('/players/integral-config'),
  updateIntegralConfig: (data) => api.put('/players/integral-config', data),
  ban: (playerId, data, targetZone) => api.post(`/players/${playerId}/ban`, { ...data, ...(targetZone ? { target_zone: targetZone } : {}) }),
  unban: (playerId, data, targetZone) => api.post(`/players/${playerId}/unban`, { ...data, ...(targetZone ? { target_zone: targetZone } : {}) }),
  updateResources: (playerId, data, targetZone) => api.post(`/players/${playerId}/resources`, { ...data, ...(targetZone ? { target_zone: targetZone } : {}) }),
  sendMail: (playerId, data, targetZone) => api.post(`/players/${playerId}/mail`, { ...data, ...(targetZone ? { target_zone: targetZone } : {}) }),
  recharge: (playerId, data, targetZone) => api.post(`/players/${playerId}/recharge`, { ...data, ...(targetZone ? { target_zone: targetZone } : {}) }),
  rechargeWithIntegral: (playerId, data, targetZone) => api.post(`/players/${playerId}/recharge-with-integral`, { ...data, ...(targetZone ? { target_zone: targetZone } : {}) }),
  resetPassword: (accountId, data) => api.post(`/players/accounts/${accountId}/reset-password`, data)
};

export const avatarReviewAPI = {
  getList: (params) => api.get('/avatar-reviews', { params }),
  approve: (iconId, data = {}) => api.post(`/avatar-reviews/${iconId}/approve`, data),
  reject: (iconId, data = {}) => api.post(`/avatar-reviews/${iconId}/reject`, data)
};

export const configAPI = {
  getSystem: () => api.get('/configs/system'),
  updateSystem: (name, data) => api.put(`/configs/system/${name}`, data),
  getLoginAccess: () => api.get('/configs/login-access'),
  updateLoginAccess: (data) => api.put('/configs/login-access', data),
  getClientUpdates: () => api.get('/configs/client-updates'),
  updateClientUpdate: (target, data) => api.put(`/configs/client-updates/${target}`, data),
  getPublicClientUpdate: (params) => api.get('/configs/public/client-update', { params }),
  getActivities: () => api.get('/configs/activities'),
  getItems: () => api.get('/configs/items'),
  getFrames: () => api.get('/configs/frames'),
  getGameServerUrl: () => api.get('/configs/game-server-url')
};

export const userCenterAPI = {
  getLinks: () => api.get('/user-center/links'),
  createLink: (data) => api.post('/user-center/links', data),
  updateLink: (id, data) => api.put(`/user-center/links/${id}`, data),
  deleteLink: (id) => api.delete(`/user-center/links/${id}`)
};

export const apiControlAPI = {
  getEndpoints: () => api.get('/api-controls'),
  syncEndpoints: () => api.post('/api-controls/sync'),
  updateEndpoint: (id, data) => api.put(`/api-controls/${id}`, data)
};

export const statsAPI = {
  getOverview: () => api.get('/stats/overview'),
  getRegistrationTrend: () => api.get('/stats/registration-trend'),
  getPaymentTrend: () => api.get('/stats/payment-trend'),
  getServerStats: () => api.get('/stats/server-stats'),
  getLevelDistribution: () => api.get('/stats/level-distribution'),
  getPaymentStats: () => api.get('/stats/payment-stats')
};

export const accountAPI = {
  getList: (params) => api.get('/accounts', { params }),
  getDetail: (id) => api.get(`/accounts/${id}`),
  update: (id, data) => api.put(`/accounts/${id}`, data),
  ban: (id, data) => api.post(`/accounts/${id}/ban`, data),
  sendMail: (id, data) => api.post(`/accounts/${id}/send-mail`, data),
  sendItem: (id, data) => api.post(`/accounts/${id}/send-item`, data)
};

export const mailAPI = {
  getList: (params) => api.get('/mails', { params }),
  send: (data) => api.post('/mails/send', data),
  delete: (id) => api.delete(`/mails/${id}`),
  getLogs: (params) => api.get('/mails/logs', { params }),
  getScheduledTasks: (params) => api.get('/mails/scheduled-tasks', { params }),
  deleteScheduledTask: (id) => api.delete(`/mails/scheduled-tasks/${id}`),
  sendFrame: (data) => api.post('/mails/send-frame', data),
  sendAoz: (data) => api.post('/mails/send-aoz', data)
};

export const mallAPI = {
  getStats: (params) => api.get('/mall/stats', { params: compactParams(params || {}) }),
  getOrderSummary: (params) => api.get('/mall/order-summary', { params }),
  createManualOrder: (data) => api.post('/mall/manual-orders', data),
  getProducts: (params) => api.get('/mall/products', { params }),
  getProduct: (id) => api.get(`/mall/products/${id}`),
  createProduct: (data) => api.post('/mall/products', data),
  updateProduct: (id, data) => api.put(`/mall/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/mall/products/${id}`),
  batchUpdateStatus: (data) => api.put('/mall/products/batch/status', data),
  getOrders: (params) => api.get('/mall/orders', { params }),
  lookupOrders: (params) => api.get('/mall/order-lookup', { params: compactParams(params) }),
  getOrderOptions: () => api.get('/mall/order-options'),
  getOrder: (id, params) => api.get(`/mall/orders/${id}`, { params: compactParams(params) }),
  updateOrderStatus: (id, data) => api.put(`/mall/orders/${id}/status`, data),
  getBanners: (params) => api.get('/mall/banners', { params }),
  createBanner: (data) => api.post('/mall/banners', data),
  updateBanner: (id, data) => api.put(`/mall/banners/${id}`, data),
  deleteBanner: (id) => api.delete(`/mall/banners/${id}`),
  getPaymentProviderConfigs: () => api.get('/payment/provider-configs'),
  createPaymentProviderConfig: (data) => api.post('/payment/provider-configs', data),
  updatePaymentProviderConfig: (id, data) => api.put(`/payment/provider-configs/${encodeURIComponent(id)}`, data),
  deletePaymentProviderConfig: (id) => api.delete(`/payment/provider-configs/${encodeURIComponent(id)}`)
};

export const platformCurrencyAPI = {
  getManualGrants: (params) => api.get('/platform-currency/admin/manual-grants', { params: compactParams(params) }),
  createManualGrant: (data) => api.post('/platform-currency/admin/manual-grants', data),
  // ---- 玩家余额管理（管理后台余额管理页） ----
  // 查询玩家余额：游戏内 AOZ/黄金等 + 平台币 + 真实充值统计。
  //   params { serverId, keyword, matchType }。
  // 充值/扣减：body { serverId, keyword, matchType, currency, amount(>0充/<0扣), remark, requestId? }。
  //   currency: aoz|free_aoz|gm_aoz|gold|free_gold|luck_coin|platform_coin。
  // 记录：params { serverId, keyword, matchType, page?, limit? } → items=充扣日志, manualGrants=人工充值订单, payOrders=支付订单。
  queryBalance: (params) => api.get('/platform-currency/admin/balance', { params: compactParams(params) }),
  chargeBalance: (data) => api.post('/platform-currency/admin/balance/charge', data),
  balanceRecords: (params) => api.get('/platform-currency/admin/balance/records', { params: compactParams(params) })
};

export const manualSupportAdminAPI = {
  // 客服余额（渠道共享钱包）
  getChannelWallets: () => api.get('/manual-support/admin/channel-wallets'),
  // 客服余额流水（days 1-30）
  getChannelWalletLogs: (params) => api.get('/manual-support/admin/channel-wallet-logs', { params: compactParams(params || {}) }),
  // 转入/扣回客服余额（amount>0 转入，<0 扣回）
  creditChannelWallet: (channel, data) => api.post(`/manual-support/admin/channel-wallets/${encodeURIComponent(channel)}/credit`, data)
};

export const lotteryAPI = {
  getActivities: (params) => api.get('/lottery/activities', { params }),
  getActivity: (id) => api.get(`/lottery/activities/${id}`),
  createActivity: (data) => api.post('/lottery/activities', data),
  updateActivity: (id, data) => api.put(`/lottery/activities/${id}`, data),
  deleteActivity: (id) => api.delete(`/lottery/activities/${id}`),
  getPrizes: (activityId) => api.get('/lottery/prizes', { params: { activity_id: activityId } }),
  createPrize: (data) => api.post('/lottery/prizes', data),
  updatePrize: (id, data) => api.put(`/lottery/prizes/${id}`, data),
  deletePrize: (id) => api.delete(`/lottery/prizes/${id}`),
  getRecords: (params) => api.get('/lottery/records', { params }),
  publicActivities: () => api.get('/lottery/public/activities'),
  publicPrizes: (activityId) => api.get(`/lottery/public/prizes/${activityId}`),
  draw: (data) => api.post('/lottery/public/draw', data)
};

export const activityAPI = {
  getActivities: (params) => api.get('/activities', { params }),
  getActivity: (id) => api.get(`/activities/${id}`),
  createActivity: (data) => api.post('/activities', data),
  updateActivity: (id, data) => api.put(`/activities/${id}`, data),
  deleteActivity: (id) => api.delete(`/activities/${id}`),
  getActivityTypes: () => api.get('/activities/types'),
  getServers: () => api.get('/activities/servers')
};

export const bubbleRewardAPI = {
  getConfig: () => api.get('/bubble-rewards/config'),
  saveConfig: (data) => api.put('/bubble-rewards/config', data),
  runNow: () => api.post('/bubble-rewards/run-now'),
  getOnlineUsers: (params) => api.get('/bubble-rewards/online-users', { params }),
  getVipRecords: (params) => api.get('/bubble-rewards/vip-records', { params }),
  grantManualVip: (data) => api.post('/bubble-rewards/vip-records/manual-grant', data),
  deactivateVip: (userId) => api.post(`/bubble-rewards/vip-records/${userId}/deactivate`),
  getLogs: (params) => api.get('/bubble-rewards/logs', { params })
};

export const publicAPI = {
  getCaptcha: () => api.get('/public/captcha'),
  verifyCaptcha: (data) => api.post('/public/captcha/verify', data),
  verifyInviteCode: (code) => api.get('/public/verify-invite-code', { params: { code } }),
  register: (data) => api.post('/public/register', data),
  getAgentByCode: (code) => api.get(`/public/agent/${code}`),
  accountLogin: (data) => api.post('/login/account-login', data),
  clientLogin: (data) => api.post('/login/client-login', data),
  getRecommendServer: (data) => api.post('/login/get-recommend-server', data),
  getAllServerList: (data) => api.post('/login/get-all-server-list', data),
  createAccount: (data) => api.post('/login/create-account', data),
  getMallProducts: (params) => api.get('/mall/public/products', { params }),
  getMallBanners: () => api.get('/mall/public/banners'),
  // 金额分档(shop1.html 同款): 可带 { amount, language } —— 不传/空则后端返回全量(严格向后兼容)。
  getPaymentProviders: (params) => api.get('/mall/public/payment-providers', { params: compactParams(params || {}) }),
  // 人工充值(shop1.html 同款): 公共渠道列表 + 建会话(AOZ 变体带 productId, 服务端凭 authKey 派生账号/角色)。
  getManualChannels: (params) => api.get('/manual-recharge/public/channels', { params: compactParams(params || {}) }),
  createManualSession: (data) => api.post('/manual-recharge/public/sessions', data),
  getFirstCharge: (params) => api.get('/mall/public/first-charge', { params }),
  getCurrentMallAccount: (params) => api.get('/mall/public/current-account', { params }),
  searchAccount: (data) => api.post('/mall/public/search-account', data),
  getCharacters: (params) => api.get('/mall/public/characters', { params }),
  createOrder: (data) => api.post('/mall/public/orders', data),
  exchangeWebHandoff: (token) => api.post('/login/web-session/handoff/exchange', { token }),
  getPlatformSummary: (params) => api.get('/platform-currency/public/summary', { params }),
  getPlatformOrder: (orderNo, params) => api.get(`/platform-currency/public/orders/${encodeURIComponent(orderNo)}`, { params }),
  createPlatformTopup: (data) => api.post('/platform-currency/public/topup-orders', data)
};

export const giftAPI = {
  getList: (params) => api.get('/gifts', { params }),
  create: (data) => api.post('/gifts', data),
  update: (id, data) => api.put(`/gifts/${id}`, data),
  delete: (id) => api.delete(`/gifts/${id}`),
  getLogs: (params) => api.get('/gifts/logs', { params }),
  searchUser: (data) => api.post('/gifts/search-user', data),
  getAvailable: (gameUserId, params) => api.get(`/gifts/available/${gameUserId}`, { params }),
  claim: (data) => api.post('/gifts/claim', data)
};

export const cdkAPI = {
  getList: (params) => api.get('/cdks', { params: compactParams(params) }),
  getDetail: (id) => api.get(`/cdks/${id}`),
  create: (data) => api.post('/cdks', data),
  update: (id, data) => api.put(`/cdks/${id}`, data),
  delete: (id) => api.delete(`/cdks/${id}`),
  getLogs: (params) => api.get('/cdks/logs/list', { params: compactParams(params) }),
  retryClaim: (id) => api.post(`/cdks/claims/${id}/retry`),
  publicClaim: (data) => api.post('/public-cdks/claim', data)
};

export const itemAPI = {
  getList: (params) => api.get('/items', { params }),
  getCategories: () => api.get('/items/categories')
};

export const securityAPI = {
  cheatScan: (data) => api.post('/security/cheat-scan', data),
  clearItems: (data) => api.post('/security/clear-items', data),
  clearItemsPreview: (data) => api.post('/security/clear-items/preview', data)
};

// 人工代充(需求一): 代理端(客服)使用 agent 令牌; admin 端使用 admin 令牌。
// 代理端 URL 以 /manual-recharge/agent 开头, 由上方 getRequestRole 自动切到 agent 令牌。
export const csDeskAPI = {
  getMe: () => api.get('/cs-desk/me'),
  getAgents: () => api.get('/cs-desk/agents'),
  getChannels: () => api.get('/cs-desk/channels'),
  getLedgers: (params) => api.get('/cs-desk/ledgers', { params: compactParams(params) })
};

export const manualRechargeAPI = {
  // ---- 代理(客服)端 ----
  getSessions: (params) => api.get('/manual-recharge/agent/sessions', { params: compactParams(params) }),
  getMessages: (sessionNo, params) => api.get(`/manual-recharge/agent/sessions/${encodeURIComponent(sessionNo)}/messages`, { params: compactParams(params) }),
  postMessage: (sessionNo, data) => api.post(`/manual-recharge/agent/sessions/${encodeURIComponent(sessionNo)}/messages`, data),
  complete: (sessionNo) => api.post(`/manual-recharge/agent/sessions/${encodeURIComponent(sessionNo)}/complete`),
  cancel: (sessionNo, data) => api.post(`/manual-recharge/agent/sessions/${encodeURIComponent(sessionNo)}/cancel`, data),
  // 修改订单金额(客服): body { amount: 整数>0 }。仅平台币变体; 成功后返回 { amount, oldAmount, ... }。
  updateAmount: (sessionNo, data) => api.post(`/manual-recharge/agent/sessions/${encodeURIComponent(sessionNo)}/amount`, data),
  // 补单(客服): 凭"账号 / 账号ID"直接补发平台币并落一笔已完成订单(计入玩家所属代理业绩)。
  //   body { identifier(账号名或账号ID), quantity(整数>0), requestId(8-64位[A-Za-z0-9._:-], 幂等键), remark? }。
  //   口径: 玩家到账=数量×1.03(多送3%), 操作客服钱包扣 数量×1.03; 业绩金额(total_amount)=数量(基数)。
  reissue: (data) => api.post('/manual-recharge/agent/reissue', data),
  typing: (sessionNo) => api.post(`/manual-recharge/agent/sessions/${encodeURIComponent(sessionNo)}/typing`),
  // 需求3: 客服端联系方式备注(side 由后端强制=agent; account_id 由会话派生, 防伪造/越权)。
  //   POST body: { channel: 'wechat'|'alipay', account: <=128字 }; GET/POST 均返回 { notes:[{side,channel,account,updateTime}] }。
  getContactNotes: (sessionNo) => api.get(`/manual-recharge/agent/sessions/${encodeURIComponent(sessionNo)}/contact-notes`),
  saveContactNote: (sessionNo, data) => api.post(`/manual-recharge/agent/sessions/${encodeURIComponent(sessionNo)}/contact-notes`, data),
  // 自定义备注(微信备注名式): 客服给该玩家起自由文本备注(side 后端强制=agent; last-write-wins)。
  //   POST body: { remark: <=200字 }; 返回 { my_remark }。读取走会话列表的 session.myRemark。
  saveRemark: (sessionNo, data) => api.post(`/manual-recharge/agent/sessions/${encodeURIComponent(sessionNo)}/remark`, data),
  // 快捷回复(按客服 agent_id 私有, 无条数上限): 列表/追加/删除/重排。
  //   后端为嵌套信封 { success, message, data:{...} }:
  //   GET  → data.items:[{id, content, sortOrder}](sort_order asc, id asc)。
  //   POST body { content }(trim 后 1..1000 字)追加到末尾 → data.item。
  //   DELETE :id 仅删自己(看 success)。 PUT /reorder body { ids:[...] } 必须恰为自己全部 reply id 集合(看 success)。
  getQuickReplies: () => api.get('/manual-recharge/agent/quick-replies'),
  addQuickReply: (data) => api.post('/manual-recharge/agent/quick-replies', data),
  deleteQuickReply: (id) => api.delete(`/manual-recharge/agent/quick-replies/${encodeURIComponent(id)}`),
  reorderQuickReplies: (data) => api.put('/manual-recharge/agent/quick-replies/reorder', data),
  // 编辑单条快捷回复内容(按客服私有): PUT :id body { content }(trim 后 1..1000 字) → data.item。
  editQuickReply: (id, data) => api.put('/manual-recharge/agent/quick-replies/' + encodeURIComponent(id), data),
  // 联系方式发送(新版, 取代旧 contact-notes; 旧端点保留不删): 生成 [[CONTACT]] marker 消息。
  //   body { channel: 'wechat'|'alipay', text, imageUrl }; text/imageUrl 至少一非空。
  sendContact: (sessionNo, data) => api.post(`/manual-recharge/agent/sessions/${encodeURIComponent(sessionNo)}/contact`, data),
  // 联系方式图片上传: 契约冻结为原始二进制(Content-Type=file.type)绕过全局 express.json; 不走 axios(避免 JSON 头/转码)。
  //   返回嵌套信封 { success, message, data:{ url:"/uploads/contact/<file>" } }。
  uploadContactImage: async (sessionNo, file) => {
    const token = getStoredToken('agent');
    const resp = await fetch(`${API_BASE_URL}/manual-recharge/agent/sessions/${encodeURIComponent(sessionNo)}/contact-image`, {
      method: 'POST',
      headers: {
        'Content-Type': file.type,
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: file
    });
    return resp.json();
  },
  setAccepting: (data) => api.put('/manual-recharge/agent/accepting', data),
  getWallet: (params) => api.get('/manual-recharge/agent/wallet', { params: compactParams(params) }),
  transfer: (data) => api.post('/manual-recharge/agent/transfer', data),
  // ---- 需求2/3/4: 服务玩家列表 / 客服订单统计 / 通道转账统计(均 agent 段, 自动切 agent 令牌) ----
  // 需求2.2: 我服务过的玩家(服务端受控分页) + 单玩家订单明细(全部状态)。
  getServedPlayers: (params) => api.get('/manual-recharge/agent/served-players', { params: compactParams(params) }),
  getServedPlayerOrders: (accountId, params) => api.get(`/manual-recharge/agent/served-players/${encodeURIComponent(accountId)}/orders`, { params: compactParams(params) }),
  // 需求3: 我的订单统计(客服维度, level2 可聚合下级 + 仅本人)。
  getOrderStats: (params) => api.get('/manual-recharge/agent/order-stats', { params: compactParams(params) }),
  // 需求4: 通道管理员 ↔ 下级 转账 / 共享额度借用统计(双方可见, 后端收敛对手方范围)。
  getTransferStats: (params) => api.get('/manual-recharge/agent/transfer-stats', { params: compactParams(params) }),
  // EventSource 无法带 header, 客服 SSE 用 query token 鉴权(仿 serverAPI.getStatusStreamUrl)。
  getAgentStreamUrl: (sessionNo, token) => {
    const searchParams = new URLSearchParams({
      token: token || '',
      session_no: sessionNo || ''
    });
    return `${API_BASE_URL}/manual-recharge/agent/stream?${searchParams.toString()}`;
  },
  // ---- admin(总管)端 ----
  getChannels: () => api.get('/manual-recharge/admin/channels'),
  createChannel: (data) => api.post('/manual-recharge/admin/channels', data),
  updateChannel: (id, data) => api.put(`/manual-recharge/admin/channels/${encodeURIComponent(id)}`, data),
  setWeights: (id, data) => api.put(`/manual-recharge/admin/channels/${encodeURIComponent(id)}/weights`, data),
  grant: (agentId, data) => api.post(`/manual-recharge/admin/agents/${encodeURIComponent(agentId)}/grant`, data),
  // R4: 管理员只读查询 —— 全部客服订单记录 / 全部发放记录(admin 令牌; URL 不含 /agent 前缀, 拦截器自动用 admin token)。
  listAdminOrders: (params) => api.get('/manual-recharge/admin/orders', { params: compactParams(params) }),
  listAdminGrants: (params) => api.get('/manual-recharge/admin/grants', { params: compactParams(params) }),
  // 客服余额总览(只读): 所有人工客服(含通道管理员)+ 各自 agent_coin_wallet 余额(元), 便于管理员及时补充。
  listAgentBalances: () => api.get('/manual-recharge/admin/agent-balances')
};

export default api;
